import { Loadable } from '@/components/loaders';
import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { FrontTable } from '@/lib/Tables';
import { Audience } from '@/models/audiences';
import { ConnectorFlowInstanceDTO, ConnectorFlowInstanceJobStatusDTO } from '@/models/connectors';
import { DistributionChannel } from '@/models/distributionChannels';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AudienceDestinationTableCaption } from './AudienceDestinationTableCaption';
import { CONNECTOR_DESTINATION_CUSTOM, CONNECTOR_TYPE_CUSTOM } from './constants';
import { getDestinationTableColumns, UnifiedDestinationData } from './helpers';

type AudienceDestinationTableProps = {
  destinations: DistributionChannel[];
  flowInstances: ConnectorFlowInstanceDTO[];
  jobStatuses: ConnectorFlowInstanceJobStatusDTO[];
  audience?: Audience;
  isLoading: boolean;
};

export const AudienceDestinationTable: FC<AudienceDestinationTableProps> = ({ destinations, flowInstances, jobStatuses, audience, isLoading }) => {
  const { t } = useTranslation();
  const isWorkspaceAdmin = useWorkspaceAdmin();

  const jobStatusMap = useMemo(() => {
    const map = new Map<string, ConnectorFlowInstanceJobStatusDTO>();
    for (const status of jobStatuses) {
      map.set(status.connectorFlowInstanceId, status);
    }
    return map;
  }, [jobStatuses]);

  const tableData = useMemo(() => {
    const combinedData: UnifiedDestinationData[] = [
      ...destinations.map((dest) => ({
        id: dest.id,
        name: dest.name,
        type: dest.destination,
        destination: dest.destination,
        createdOn: dest.createdOn,
        data: dest,
      })),
      ...flowInstances.map((flow) => ({
        id: flow.id,
        name: flow.connectorName,
        type: CONNECTOR_TYPE_CUSTOM,
        destination: CONNECTOR_DESTINATION_CUSTOM,
        createdOn: flow.createdAt,
        data: flow,
        jobStatus: jobStatusMap.get(flow.id),
      })),
    ];
    return combinedData;
  }, [destinations, flowInstances, jobStatusMap]);

  const columns = useMemo(() => getDestinationTableColumns(t, audience, isWorkspaceAdmin), [t, audience, isWorkspaceAdmin]);

  return (
    <Loadable isLoading={isLoading}>
      <FrontTable
        caption={<AudienceDestinationTableCaption destinations={destinations} flowInstances={flowInstances} />}
        tableData={tableData}
        columns={columns}
        isLoading={false}
        notFound={t('audience_details:destinations.empty')}
      />
    </Loadable>
  );
};
